import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { toast } from 'react-toastify';
import {
    UserCircleIcon,
    MagnifyingGlassIcon,
    ArrowPathIcon,
    BriefcaseIcon
} from '@heroicons/react/24/outline';

const ManageLeaveBalances = () => {
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [balances, setBalances] = useState([]);
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(null); // track which specific balance is updating

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const res = await api.get('/admin/users');
            setUsers(res.data);
        } catch (err) {
            toast.error("Failed to load users list");
        }
    };

    const handleUserChange = async (userId) => {
        setSelectedUserId(userId);
        if (!userId) {
            setBalances([]);
            return;
        }

        setLoading(true);
        try {
            const res = await api.get(`/admin/users/${userId}/balances`);
            setBalances(res.data);
        } catch (err) {
            console.error(err);
            toast.error("Failed to fetch user balances");
        } finally {
            setLoading(false);
        }
    };

    const handleBalanceChange = (id, value) => {
        setBalances(balances.map(b =>
            b.id === id ? { ...b, remaining_count: value } : b
        ));
    };

    const updateBalance = async (balanceId, newCount) => {
        setUpdating(balanceId);
        try {
            await api.post('/admin/users/balances/update', {
                balance_id: balanceId,
                new_count: newCount
            });
            toast.success("Balance updated successfully");
        } catch (err) {
            toast.error("Update failed");
        } finally {
            setUpdating(null);
        }
    };

    return (
        <Layout>
            <div className="max-w-4xl mx-auto">
                <div className="mb-8 border-b border-slate-200 pb-4">
                    <h1 className="text-2xl font-black text-slate-800">Manage Employee Balances</h1>
                    <p className="text-slate-500 text-sm mt-1">Select an employee to manually adjust their leave entitlements.</p>
                </div>

                {/* User Selector */}
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 mb-8">
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Select Employee</label>
                    <div className="relative">
                        <UserCircleIcon className="w-5 h-5 absolute left-3 top-3 text-slate-400" />
                        <select
                            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                            value={selectedUserId}
                            onChange={(e) => handleUserChange(e.target.value)}
                        >
                            <option value="">-- Choose an Employee --</option>
                            {users.map(u => (
                                <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="flex justify-center py-10 text-slate-400 animate-pulse">
                        <ArrowPathIcon className="w-8 h-8 animate-spin" />
                    </div>
                )}

                {/* Balance Cards */}
                {!loading && selectedUserId && balances.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {balances.map(b => (
                            <div key={b.id} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                            {b.name}
                                        </h3>
                                        <div className="flex gap-2 mt-1">
                                            {b.is_carry_forward ? <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">Carry Fwd</span> : null}
                                            {b.is_comp_off ? <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">Comp Off</span> : null}
                                        </div>
                                    </div>
                                    <div className="p-2 bg-slate-50 rounded-lg">
                                        <BriefcaseIcon className="w-6 h-6 text-slate-300" />
                                    </div>
                                </div>

                                <div className="mt-2">
                                    <label className="text-xs text-slate-400 font-bold uppercase">Remaining Days</label>
                                    <div className="flex gap-2 mt-1">
                                        <input
                                            type="number"
                                            step="0.5"
                                            className="w-full p-2 border border-slate-300 rounded-lg font-bold text-slate-800 text-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                                            value={b.remaining_count}
                                            onChange={(e) => handleBalanceChange(b.id, e.target.value)}
                                        />
                                        <button
                                            onClick={() => updateBalance(b.id, b.remaining_count)}
                                            disabled={updating === b.id}
                                            className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                                        >
                                            {updating === b.id ? <ArrowPathIcon className="w-5 h-5 animate-spin" /> : 'Update'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && selectedUserId && balances.length === 0 && (
                    <div className="text-center py-10 text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-300">
                        No leave policies assigned to this user.
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default ManageLeaveBalances;